import { CircleAlert, CircleCheck, Info } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cn } from '../cn';

type ToastKind = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  kind: ToastKind;
  title: string;
  text?: string;
}

interface ToastApi {
  success: (title: string, text?: string) => void;
  error: (title: string, text?: string) => void;
  info: (title: string, text?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
let seq = 0;

/** Avisos breves al confirmar una acción ("Sucursal creada") o al fallar. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const push = useCallback((kind: ToastKind, title: string, text?: string) => {
    const id = ++seq;
    setItems((prev) => [...prev.slice(-3), { id, kind, title, text }]);
    window.setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), kind === 'error' ? 7000 : 4500);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (t, x) => push('success', t, x),
      error: (t, x) => push('error', t, x),
      info: (t, x) => push('info', t, x),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="bf-toaster" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={cn('bf-toast', `bf-toast--${t.kind}`)}>
            {t.kind === 'success' ? <CircleCheck /> : t.kind === 'error' ? <CircleAlert /> : <Info />}
            <div>
              <p className="bf-toast-title">{t.title}</p>
              {t.text && <p className="bf-toast-text">{t.text}</p>}
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast necesita ToastProvider');
  return ctx;
}
